import clsx from 'clsx'
import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import CheckCircleIcon from '@/assets/icons/check-circle.svg?react'
import NotificationIcon from '@/assets/icons/notification.svg?react'
import DataBlock from '@/components/DataBlock/DataBlock'

import styles from './SchoolDayPreview.module.scss'
import type { TimetableSettings } from '../queries/timetableSettingsQueryOptions'
import {
  type ActivityType,
  type Activity,
  createSchoolDay
} from '../utils/create-school-day'

type SchoolDayPreviewProps = {
  timetableSettings: TimetableSettings
}

const SchoolDayPreview = (props: SchoolDayPreviewProps) => {
  const { t } = useTranslation('schoolSettings')

  const infoArray = useMemo(() => {
    const schoolDay = createSchoolDay(props.timetableSettings)

    return schoolDay.map(activity => ({
      label:
        activity.type === 'lesson'
          ? t(`label.lesson-n`, { ORDINAL: activity.ordinal })
          : t(`label.special-break`),
      value: <Activity {...activity} />
    }))
  }, [props.timetableSettings, t])

  return <DataBlock infoArray={infoArray} />
}

type ActivityProps = Activity

const Activity = (props: ActivityProps) => (
  <div className={styles.activity}>
    <ActivityBadge type={props.type} />
    <span className={styles.activityTime}>
      {props.from} - {props.to}
    </span>
  </div>
)

const ACTIVITY_ICON_MAP: Record<
  ActivityType,
  React.FC<React.SVGProps<SVGSVGElement>>
> = {
  lesson: CheckCircleIcon,
  specialBreak: NotificationIcon
}

type ActivityBadgeProps = {
  type: ActivityType
}

const ActivityBadge = (props: ActivityBadgeProps) => {
  const Icon = ACTIVITY_ICON_MAP[props.type]

  return (
    <span className={clsx(styles.badge, styles[props.type])}>
      <Icon aria-hidden="true" />
    </span>
  )
}

export default memo(SchoolDayPreview)
